import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionChangedPayload {
  oldEndpoint: string | null;
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent?: string;
}

/**
 * Listens for service-worker messages:
 *  - `push-navigate`: a notification was tapped → route in-app via React Router.
 *  - `push-subscription-changed`: the browser rotated the push subscription →
 *    persist the new endpoint so background pushes keep being delivered.
 */
export const PushNavigationBridge = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const onNativePushNavigation = (event: Event) => {
      const url = (event as CustomEvent<{ url?: string }>).detail?.url;
      if (url?.startsWith("/")) navigate(url);
    };
    window.addEventListener("everafterlist:push-navigation", onNativePushNavigation);
    try {
      const pendingUrl = window.sessionStorage.getItem("pending_push_navigation");
      if (pendingUrl?.startsWith("/")) {
        window.sessionStorage.removeItem("pending_push_navigation");
        navigate(pendingUrl);
      }
    } catch {
      // Navigation still works through the live event when storage is unavailable.
    }

    if (!("serviceWorker" in navigator)) {
      return () => window.removeEventListener("everafterlist:push-navigation", onNativePushNavigation);
    }

    const onMessage = async (event: MessageEvent) => {
      const data = event.data as
        | { type?: string; url?: string; payload?: SubscriptionChangedPayload }
        | undefined;
      if (!data?.type) return;

      if (data.type === "push-navigate" && data.url) {
        try {
          const u = new URL(data.url, window.location.origin);
          if (u.origin === window.location.origin) {
            navigate(u.pathname + u.search + u.hash);
          } else {
            window.location.href = data.url;
          }
        } catch {
          navigate(data.url);
        }
        return;
      }

      if (data.type === "push-subscription-changed" && data.payload) {
        try {
          const { data: auth } = await supabase.auth.getUser();
          const userId = auth?.user?.id;
          if (!userId) return;

          const p = data.payload;
          if (p.oldEndpoint && p.oldEndpoint !== p.endpoint) {
            await supabase
              .from("push_subscriptions")
              .delete()
              .eq("endpoint", p.oldEndpoint);
          }
          await supabase.from("push_subscriptions").upsert(
            {
              user_id: userId,
              endpoint: p.endpoint,
              p256dh: p.keys.p256dh,
              auth: p.keys.auth,
              user_agent: p.userAgent || navigator.userAgent,
              last_used_at: new Date().toISOString(),
            },
            { onConflict: "endpoint" },
          );
        } catch (err) {
          console.error("Failed to persist rotated push subscription", err);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("everafterlist:push-navigation", onNativePushNavigation);
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [navigate]);

  return null;
};
